import React, { useCallback, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import StructuredDataTree from './StructuredDataTree';
import BoundArrayTable from './BoundArrayTable';
import RegistryValidationSummary from './RegistryValidationSummary';
import {
  getAtConcretePath,
  getAtPath,
  indexInstances,
  instanceValueChanged,
  omitAtPath,
  setAtPath,
  tableBindings
} from '../../utils/registryBindings';

const EMPTY_ROWS = [];
const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

/**
 * Reviewer view of a JSON attribute with registry bindings (§9.1):
 * validation summary + one BoundArrayTable per table root + StructuredDataTree
 * for the remaining keys (table keys removed, merged back on change).
 */
const BoundJsonAttribute = ({ value, bindings, validation, isEditing, onChange, onJsonError }) => {
  const instances = useMemo(
    () => (Array.isArray(validation?.instances) ? validation.instances : []),
    [validation]
  );
  const instanceIndex = useMemo(() => indexInstances(instances), [instances]);
  const candidateTables = useMemo(() => tableBindings(bindings).tables, [bindings]);

  // A root becomes a table when it is absent (empty table) or an array of objects;
  // anything else stays in the tree. Memoized on its real inputs so identity is
  // stable across unrelated re-renders (e.g. `remainder` below feeds
  // `StructuredDataTree`'s `[data]` effects, which must not re-run every render).
  const tables = useMemo(() => candidateTables.filter((t) => {
    const rows = getAtPath(value, t.root);
    return rows === undefined || (Array.isArray(rows) && rows.every(isPlainObject));
  }), [candidateTables, value]);
  const inTable = (path) => tables.some((t) => typeof path === 'string' && path.startsWith(`${t.root}[`));

  // Defaults on only when at least one table has an invalid marker under its own
  // root; an invalid instance elsewhere (e.g. a non-table scalar) must not hide
  // otherwise-valid table rows behind the filter.
  const [onlyInvalid, setOnlyInvalid] = useState(() => instances.some((inst) => inTable(inst.path)));
  const [focusRequest, setFocusRequest] = useState(null);
  const [modifiedByRoot, setModifiedByRoot] = useState({});

  const handleModified = useCallback((root, count) => {
    setModifiedByRoot((prev) => (prev[root] === count ? prev : { ...prev, [root]: count }));
  }, []);

  const tableModified = tables.reduce((sum, t) => sum + (modifiedByRoot[t.root] || 0), 0);
  const otherModified = instances.filter(
    (inst) => !inTable(inst.path) && instanceValueChanged(getAtConcretePath(value, inst.path), inst)
  ).length;

  const jump = (instance) => {
    setOnlyInvalid(true);
    setFocusRequest((prev) => ({ path: instance.path, nonce: (prev?.nonce || 0) + 1 }));
  };

  const remainder = useMemo(
    () => tables.reduce((acc, t) => omitAtPath(acc, t.root), value),
    [tables, value]
  );

  const handleTableChange = (root) => (rows) => onChange(setAtPath(value, root, rows));
  const handleTreeChange = (nextRemainder) => {
    onChange(tables.reduce((acc, t) => {
      const rows = getAtPath(value, t.root);
      return rows === undefined ? acc : setAtPath(acc, t.root, rows);
    }, nextRemainder));
  };

  return (
    <Box>
      <RegistryValidationSummary
        validation={validation}
        isEditing={isEditing}
        modifiedCount={tableModified + otherModified}
        onlyInvalid={onlyInvalid}
        onOnlyInvalidChange={tables.length > 0 ? setOnlyInvalid : undefined}
        onJump={jump}
        canJump={(instance) => inTable(instance.path)}
      />
      {tables.map((t) => (
        <Box key={t.root} sx={{ mb: 2 }}>
          <BoundArrayTable
            root={t.root}
            rows={getAtPath(value, t.root) || EMPTY_ROWS}
            columns={t.columns}
            instanceIndex={instanceIndex}
            editable={isEditing}
            onChange={isEditing ? handleTableChange(t.root) : undefined}
            onlyInvalid={onlyInvalid}
            focusRequest={focusRequest}
            onModifiedCountChange={handleModified}
          />
        </Box>
      ))}
      {isPlainObject(remainder) && Object.keys(remainder).length > 0 && (
        <StructuredDataTree
          data={remainder}
          editable={isEditing}
          onChange={isEditing ? handleTreeChange : undefined}
          showJsonToggle={isEditing}
          onJsonError={onJsonError}
        />
      )}
    </Box>
  );
};

export default BoundJsonAttribute;
